import { ReactNode } from "react";
import { useIsDevCycleInitialized } from "@devcycle/react-client-sdk";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation } from "@tanstack/react-router";

import CentredSpinner from "lib/core_components/CentredSpinner";
import { REQUIRE_BETTER_AUTH_SESSION, useVariableValue } from "lib/featureFlags";
import {
  betterAuthSessionQueryOptions,
  hasNoBetterAuthSession,
} from "lib/hooks/api/auth/betterAuthSessionQuery";

/**
 * Sends a user signed in to Rails but not better-auth (a Rails session that predates the move off
 * Auth0) to log in again. Gated by a flag so the rollout can be staged.
 *
 * Checks once per page load: the session query is cached for the page's life, so a session revoked
 * mid-page is caught on the next load.
 */
export function BetterAuthSessionGate({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  // The /auth/* pages are where the user gets a session, so gating them would loop.
  const applies = window.userSignedIn === true && !location.pathname.startsWith("/auth/");

  const { data: session } = useQuery({ ...betterAuthSessionQueryOptions(), enabled: applies });
  const railsOnly = applies && session === null;

  const isDevCycleInitialized = useIsDevCycleInitialized();
  // Coerced: the e2e flag mock returns undefined rather than the declared boolean.
  const required = useVariableValue(REQUIRE_BETTER_AUTH_SESSION, false) === true;

  // The session query above reads an auth-service failure as no session, so confirm it first. A
  // failure is retried, then lets the user in: logging in needs the auth-service too.
  const { data: noSession, isError: checkFailed } = useQuery({
    queryKey: ["betterAuthSessionAbsent"],
    queryFn: hasNoBetterAuthSession,
    enabled: railsOnly && isDevCycleInitialized && required,
    staleTime: Infinity,
  });

  const spinner = (
    <FullScreenContainer>
      <CentredSpinner label="Checking your session" />
    </FullScreenContainer>
  );
  if (!applies) return <>{children}</>;
  if (session === undefined) return spinner;
  if (!railsOnly) return <>{children}</>;
  if (!isDevCycleInitialized) return spinner;
  if (!required) return <>{children}</>;
  if (checkFailed) return <>{children}</>;
  if (noSession === undefined) return spinner;
  if (!noSession) return <>{children}</>;

  // A full page load, keeping the spinner up until it lands. An SPA navigation moves useLocation()
  // to /auth/login while the login route loads, so this would render the page being left meanwhile.
  return (
    <>
      <Navigate
        to="/auth/login"
        search={{ redirect: location.pathname + location.searchStr }}
        replace
        reloadDocument
      />
      {spinner}
    </>
  );
}

// Full height, as in AppProviders' loading fallback, so the spinner stays where that one was.
const FullScreenContainer = styled.div({
  height: "100vh",
});
